import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import {
  ADMIN_SLUG,
  AI_COWORKERS_SLUG,
  ENTITLE_SLUG,
  EPML_SLUG,
  INSIGHTS_SLUG,
  PASSWORD_SAFE_SLUG,
  PLATFORM_SLUG,
  PMC_SLUG,
  PRIVILEGED_REMOTE_ACCESS_SLUG,
  ProductMenuItem,
  REMOTE_SUPPORT_SLUG,
  SECRETS_SLUG,
  SiteData,
} from '../../types';
import '../menu/defaults';
import '../menu/menu/menu';

export const NAVIGATION_MENU_CONTENT_TAG = 'platform-navigation-menu-content' as const;

/** Shared `platform-menu` composition: each product's named slot with its api/default menu content. */
@customElement(NAVIGATION_MENU_CONTENT_TAG)
export class PlatformNavigationMenuContent extends LitElement {
  @property({ attribute: false })
  productMenuItems: ProductMenuItem[] = [];

  @property({ attribute: false })
  currentSite?: SiteData;

  override render() {
    const availableProducts = this.productMenuItems.map((p) => p.slug);

    return html`
      <platform-menu .productMenuItems=${this.productMenuItems} .currentSite=${this.currentSite}>
        <slot name="admin-menu" slot="admin-menu">
          ${when(
            availableProducts.includes(ADMIN_SLUG),
            () => html`<platform-api-menu product=${ADMIN_SLUG}></platform-api-menu>`,
            () => nothing,
          )}
        </slot>
        <slot name="platform-menu" slot="platform-menu">
          ${when(
            availableProducts.includes(PLATFORM_SLUG),
            () => html`<platform-default-platform-menu></platform-default-platform-menu>`,
            () => nothing,
          )}
        </slot>

        <slot name="insights-menu" slot="insights-menu">
          ${when(
            availableProducts.includes(INSIGHTS_SLUG),
            () => html`<platform-api-menu product=${INSIGHTS_SLUG}></platform-api-menu>`,
            () => nothing,
          )}
        </slot>

        <slot name="epml-menu" slot="epml-menu">
          ${when(
            availableProducts.includes(EPML_SLUG),
            () => html`<platform-api-menu product=${EPML_SLUG}></platform-api-menu>`,
            () => nothing,
          )}
        </slot>

        <slot name="pmc-menu" slot="pmc-menu">
          ${when(
            availableProducts.includes(PMC_SLUG),
            () => html`<platform-api-menu product=${PMC_SLUG}></platform-api-menu>`,
            () => nothing,
          )}
        </slot>

        <slot name="passwordsafe-menu" slot="passwordsafe-menu">
          ${when(
            availableProducts.includes(PASSWORD_SAFE_SLUG),
            () => html`<platform-api-menu product=${PASSWORD_SAFE_SLUG}></platform-api-menu>`,
            () => nothing,
          )}
        </slot>

        <slot name="privileged-remote-access-menu" slot="privileged-remote-access-menu">
          ${when(
            availableProducts.includes(PRIVILEGED_REMOTE_ACCESS_SLUG),
            () => html`<platform-api-menu product=${PRIVILEGED_REMOTE_ACCESS_SLUG}></platform-api-menu>`,
            () => nothing,
          )}
        </slot>

        <slot name="remote-support-menu" slot="remote-support-menu">
          ${when(
            availableProducts.includes(REMOTE_SUPPORT_SLUG),
            () => html`<platform-api-menu product=${REMOTE_SUPPORT_SLUG}></platform-api-menu>`,
            () => nothing,
          )}
        </slot>

        <slot name="secrets-menu" slot="secrets-menu">
          ${when(
            availableProducts.includes(SECRETS_SLUG),
            () => html`<platform-secrets-menu></platform-secrets-menu>`,
            () => nothing,
          )}
        </slot>

        <slot name="entitle-menu" slot="entitle-menu">
          ${when(
            availableProducts.includes(ENTITLE_SLUG),
            () => html`<platform-api-menu product=${ENTITLE_SLUG}></platform-api-menu>`,
            () => nothing,
          )}
        </slot>

        <slot name="ai-coworkers-menu" slot="ai-coworkers-menu">
          ${when(
            availableProducts.includes(AI_COWORKERS_SLUG),
            () => html`<platform-api-menu product=${AI_COWORKERS_SLUG}></platform-api-menu>`,
            () => nothing,
          )}
        </slot>

        <slot name="reporting-insights-menu" slot="reporting-insights-menu">
          ${when(
            availableProducts.includes(INSIGHTS_SLUG),
            () => html`<platform-reporting-insights-menu></platform-reporting-insights-menu>`,
            () => nothing,
          )}
        </slot>
      </platform-menu>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [NAVIGATION_MENU_CONTENT_TAG]: PlatformNavigationMenuContent;
  }
}
